"use client"

import { useMemo, useEffect } from "react"
import { createPortal } from "react-dom"
import { motion, AnimatePresence } from "framer-motion"
import {
    X,
    Download,
    Mail,
    GraduationCap,
    Trophy,
    TrendingUp,
    Users,
    Clock,
    Sparkles,
    Crown,
    Target,
    Flame,
    CheckCircle2,
    Zap,
    Calendar,
    MessageCircle,
    ArrowUp,
    ArrowDown,
    AlertCircle,
    Activity,
} from "@/lib/icons"
import { normalizeStatus } from "@/lib/supabase"
import { COMPLETED_STATUSES, FOLLOW_UP_STATUSES, STATUS_LABELS } from "./constants"

// ─── Catálogo de CAPs ───
const CAP_INFO = {
    gaby: {
        territory: "Niveles bajos",
        label: "Maternal · Kinder · Pre-First",
        accent: "#d97706",
        accentSoft: "#fef3c7",
        accentTint: "rgba(217, 119, 6, 0.06)",
        accentBorder: "rgba(217, 119, 6, 0.18)",
    },
    lulu: {
        territory: "Niveles altos",
        label: "Primaria · Secundaria · Prepa",
        accent: "#0f766e",
        accentSoft: "#ccfbf1",
        accentTint: "rgba(15, 118, 110, 0.06)",
        accentBorder: "rgba(15, 118, 110, 0.18)",
    },
}

const DEFAULT_CAP = {
    territory: "Equipo Tepeyac",
    label: "General",
    accent: "#7a1b33",
    accentSoft: "#fce7f3",
    accentTint: "rgba(122, 27, 51, 0.06)",
    accentBorder: "rgba(122, 27, 51, 0.18)",
}

function getCapInfo(advisor) {
    const email = String(advisor?.email || "").toLowerCase()
    const nombre = String(advisor?.nombre || "").toLowerCase()
    if (email.includes("gaby") || nombre.includes("gaby")) return CAP_INFO.gaby
    if (email.includes("lulu") || nombre.includes("lulu")) return CAP_INFO.lulu
    return DEFAULT_CAP
}

// Score de performance (calificación tipo escolar para supervisor)
function getScore(conversion, total) {
    if (total < 3) return { letter: "—", label: "Datos insuficientes", color: "#94a3b8", bg: "rgba(148,163,184,0.1)" }
    if (conversion >= 30) return { letter: "A", label: "Excelente", color: "#10b981", bg: "rgba(16,185,129,0.12)" }
    if (conversion >= 20) return { letter: "B", label: "Muy bien", color: "#0ea5e9", bg: "rgba(14,165,233,0.12)" }
    if (conversion >= 10) return { letter: "C", label: "Aceptable", color: "#f59e0b", bg: "rgba(245,158,11,0.12)" }
    if (conversion > 0) return { letter: "D", label: "Por mejorar", color: "#f97316", bg: "rgba(249,115,22,0.12)" }
    return { letter: "—", label: "Sin conversión", color: "#94a3b8", bg: "rgba(148,163,184,0.1)" }
}

// Colores por etapa del pipeline
const PIPELINE_COLORS = {
    Nuevos: "#0ea5e9",
    Contactados: "#06b6d4",
    Citas: "#8b5cf6",
    Inscritos: "#10b981",
}

// Tiempo desde última actividad (legible)
function timeAgo(dateStr) {
    if (!dateStr) return "—"
    const diff = Date.now() - new Date(dateStr).getTime()
    const mins = Math.floor(diff / 60000)
    if (mins < 60) return `hace ${mins}m`
    const hrs = Math.floor(mins / 60)
    if (hrs < 24) return `hace ${hrs}h`
    const days = Math.floor(hrs / 24)
    return `hace ${days}d`
}

export function AdvisorProfileModal({ advisor, leads = [], teamStats = {}, onClose }) {
    useEffect(() => {
        if (!advisor) return
        const handler = (e) => { if (e.key === "Escape") onClose?.() }
        window.addEventListener("keydown", handler)
        document.body.style.overflow = "hidden"
        return () => {
            window.removeEventListener("keydown", handler)
            document.body.style.overflow = ""
        }
    }, [advisor, onClose])

    const advisorLeads = useMemo(() => {
        if (!leads || !advisor) return []
        const targetEmail = String(advisor.email || "").trim().toLowerCase()
        return leads.filter((l) => String(l.asesor_email || "").trim().toLowerCase() === targetEmail)
    }, [leads, advisor])

    const stats = useMemo(() => {
        let inscritos = 0, followUp = 0, citas = 0, nuevos = 0, contactados = 0
        let lastActivity = null
        for (const l of advisorLeads) {
            const st = normalizeStatus(l.estado_lead)
            if (COMPLETED_STATUSES.has(st)) inscritos++
            if (FOLLOW_UP_STATUSES.has(st)) followUp++
            if (st === "cita" || st === "cita_solicitada") citas++
            if (st === "nuevo") nuevos++
            if (st === "contactado") contactados++
            const ts = l.updated_at || l.created_at
            if (ts && (!lastActivity || new Date(ts) > new Date(lastActivity))) lastActivity = ts
        }
        const total = advisorLeads.length
        const conversion = total > 0 ? (inscritos / total) * 100 : 0
        const tasaCitas = total > 0 ? (citas / total) * 100 : 0
        const tasaContacto = total > 0 ? ((total - nuevos) / total) * 100 : 0
        return { total, inscritos, followUp, citas, nuevos, contactados, conversion, tasaCitas, tasaContacto, lastActivity }
    }, [advisorLeads])

    const teamComparison = useMemo(() => {
        const teamTotal = teamStats.leads || 0
        const teamInscritos = teamStats.inscritos || 0
        const teamConversion = teamTotal > 0 ? (teamInscritos / teamTotal) * 100 : 0
        const diff = stats.conversion - teamConversion
        return { teamConversion, diff, isAbove: diff >= 0 }
    }, [stats, teamStats])

    const funnel = useMemo(() => {
        const max = Math.max(stats.nuevos, stats.contactados, stats.citas, stats.inscritos, 1)
        return [
            { label: "Nuevos", value: stats.nuevos, icon: Zap, color: PIPELINE_COLORS.Nuevos, pct: (stats.nuevos / max) * 100 },
            { label: "Contactados", value: stats.contactados, icon: MessageCircle, color: PIPELINE_COLORS.Contactados, pct: (stats.contactados / max) * 100 },
            { label: "Citas", value: stats.citas, icon: Calendar, color: PIPELINE_COLORS.Citas, pct: (stats.citas / max) * 100 },
            { label: "Inscritos", value: stats.inscritos, icon: CheckCircle2, color: PIPELINE_COLORS.Inscritos, pct: (stats.inscritos / max) * 100 },
        ]
    }, [stats])

    // Insights accionables para el supervisor
    const insights = useMemo(() => {
        const list = []
        if (stats.nuevos > 0) {
            list.push({ tone: "warn", icon: AlertCircle, label: `${stats.nuevos} lead${stats.nuevos === 1 ? "" : "s"} sin contactar` })
        }
        if (stats.followUp >= 5) {
            list.push({ tone: "warn", icon: Clock, label: `${stats.followUp} en seguimiento (revisar urgentes)` })
        }
        if (stats.conversion >= 30 && stats.total >= 5) {
            list.push({ tone: "good", icon: Crown, label: "Top performer del mes" })
        }
        if (stats.tasaCitas >= 40 && stats.total >= 5) {
            list.push({ tone: "good", icon: Calendar, label: `Excelente tasa de cita (${Math.round(stats.tasaCitas)}%)` })
        }
        if (stats.total >= 3 && stats.conversion === 0) {
            list.push({ tone: "bad", icon: TrendingUp, label: "Sin inscritos este mes" })
        }
        if (stats.total === 0) {
            list.push({ tone: "info", icon: Activity, label: "Sin leads asignados" })
        }
        return list
    }, [stats])

    const achievements = useMemo(() => {
        const list = []
        if (stats.conversion >= 30) list.push({ label: "Cerradora estrella", icon: Crown, color: "#f59e0b" })
        if (stats.total >= 10) list.push({ label: "10+ leads", icon: Flame, color: "#ef4444" })
        if (stats.inscritos >= 5) list.push({ label: "5+ inscripciones", icon: Trophy, color: "#10b981" })
        if (stats.citas >= 5) list.push({ label: "Agenda activa", icon: Calendar, color: "#8b5cf6" })
        if (stats.conversion >= 15 && stats.conversion < 30) list.push({ label: "Buen ritmo", icon: TrendingUp, color: "#0ea5e9" })
        return list
    }, [stats])

    const recentLeads = useMemo(() => {
        return [...advisorLeads]
            .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
            .slice(0, 5)
    }, [advisorLeads])

    const handleExport = () => {
        if (!advisorLeads.length) return
        const headers = ["Nombre", "Teléfono", "Email", "Estado", "Nivel", "Creado"]
        const rows = advisorLeads.map((l) => [
            l.nombre_tutor || "",
            l.telefono || "",
            l.email || "",
            STATUS_LABELS[normalizeStatus(l.estado_lead)] || l.estado_lead || "",
            l.nivel_educativo || "",
            l.created_at ? new Date(l.created_at).toLocaleDateString("es-MX") : "",
        ])
        const csv = [headers, ...rows].map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n")
        const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" })
        const url = URL.createObjectURL(blob)
        const a = document.createElement("a")
        a.href = url
        a.download = `${advisor.nombre || "asesor"}_leads.csv`
        document.body.appendChild(a); a.click(); document.body.removeChild(a)
        URL.revokeObjectURL(url)
    }

    if (!advisor) return null

    const cap = getCapInfo(advisor)
    const score = getScore(stats.conversion, stats.total)
    const firstName = (advisor.nombre || "Asesor").split(" ")[0]
    const initials = (advisor.nombre || "?").split(" ").filter(Boolean).map((w) => w[0]).slice(0, 2).join("").toUpperCase()
    const conversionRounded = Math.round(stats.conversion)
    const deltaRounded = Math.round(Math.abs(teamComparison.diff))

    return createPortal(
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15 }}
                className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/70 backdrop-blur-md p-4"
                onClick={(e) => e.target === e.currentTarget && onClose?.()}
            >
                <motion.div
                    initial={{ opacity: 0, scale: 0.96, y: 12 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.96, y: 12 }}
                    transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
                    className="relative w-full max-w-3xl max-h-[90vh] overflow-hidden rounded-2xl bg-white dark:bg-slate-950 shadow-2xl border border-slate-200 dark:border-slate-800/80 flex flex-col"
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* ═══ HEADER + SCORE INLINE ═══ */}
                    <div
                        className="relative flex-shrink-0 px-5 pt-4 pb-3.5 border-b border-slate-100 dark:border-slate-800/80"
                        style={{ background: `linear-gradient(135deg, ${cap.accentTint} 0%, transparent 60%)` }}
                    >
                        <div className="absolute top-0 left-0 h-0.5 w-full" style={{ backgroundColor: cap.accent }} />

                        <button
                            type="button"
                            onClick={onClose}
                            className="absolute top-3 right-3 h-7 w-7 flex items-center justify-center rounded-full text-slate-400 hover:bg-white dark:hover:bg-slate-800 hover:text-slate-700 dark:hover:text-slate-200 transition-colors shadow-sm"
                            title="Cerrar (Esc)"
                        >
                            <X className="h-3.5 w-3.5" />
                        </button>

                        <div className="flex items-center gap-3.5">
                            <div
                                className="flex h-12 w-12 items-center justify-center rounded-xl text-white text-base font-extrabold shadow-md ring-2 ring-white/80 dark:ring-slate-900/80 flex-shrink-0"
                                style={{ backgroundColor: cap.accent }}
                            >
                                {initials}
                            </div>

                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <h2 className="text-lg font-extrabold text-slate-900 dark:text-white tracking-tight leading-none">
                                        {firstName}
                                    </h2>
                                    <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded" style={{ color: cap.accent, backgroundColor: cap.accentSoft }}>
                                        {cap.territory}
                                    </span>
                                </div>
                                <div className="flex items-center gap-2 mt-1 flex-wrap text-[10.5px] text-slate-500 dark:text-slate-400">
                                    <span className="inline-flex items-center gap-1">
                                        <GraduationCap className="h-2.5 w-2.5 opacity-60" />
                                        {cap.label}
                                    </span>
                                    {advisor.email && (
                                        <>
                                            <span className="text-slate-300 dark:text-slate-600">·</span>
                                            <span className="inline-flex items-center gap-1 truncate">
                                                <Mail className="h-2.5 w-2.5" />
                                                {advisor.email}
                                            </span>
                                        </>
                                    )}
                                    {stats.lastActivity && (
                                        <>
                                            <span className="text-slate-300 dark:text-slate-600">·</span>
                                            <span className="inline-flex items-center gap-1">
                                                <Activity className="h-2.5 w-2.5" />
                                                Activa {timeAgo(stats.lastActivity)}
                                            </span>
                                        </>
                                    )}
                                </div>
                            </div>

                            {/* Score letter (calificación visible para supervisor) */}
                            <div
                                className="flex flex-col items-center justify-center h-14 w-14 rounded-xl flex-shrink-0 ring-1"
                                style={{ backgroundColor: score.bg, borderColor: score.color, boxShadow: `inset 0 0 0 1px ${score.color}30` }}
                                title={score.label}
                            >
                                <span className="text-2xl font-black leading-none tabular-nums" style={{ color: score.color }}>
                                    {score.letter}
                                </span>
                                <span className="text-[7.5px] font-bold uppercase tracking-wider mt-0.5" style={{ color: score.color }}>
                                    Score
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* ═══ BODY ═══ */}
                    <div className="flex-1 overflow-y-auto custom-scrollbar p-3.5 grid grid-cols-1 lg:grid-cols-12 gap-3">

                        {/* COLUMNA IZQUIERDA (7) */}
                        <div className="lg:col-span-7 space-y-3">

                            {/* Hero conversión */}
                            <div className="relative rounded-xl border p-3.5 overflow-hidden" style={{ borderColor: cap.accentBorder, backgroundColor: cap.accentTint }}>
                                <div className="pointer-events-none absolute -top-8 -right-8 h-28 w-28 rounded-full blur-3xl opacity-30" style={{ backgroundColor: cap.accent }} />
                                <div className="relative">
                                    <div className="flex items-center justify-between mb-1">
                                        <p className="text-[9.5px] uppercase tracking-[0.18em] font-bold text-slate-500 dark:text-slate-400">
                                            Conversión del mes
                                        </p>
                                        <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full" style={{ color: score.color, backgroundColor: score.bg }}>
                                            {score.label}
                                        </span>
                                    </div>
                                    <div className="flex items-baseline gap-1.5">
                                        <span className="text-5xl font-extrabold tabular-nums leading-none tracking-tighter" style={{ color: cap.accent }}>
                                            {conversionRounded}
                                        </span>
                                        <span className="text-xl font-extrabold leading-none" style={{ color: cap.accent, opacity: 0.4 }}>%</span>
                                        <span className="ml-1 text-[10.5px] text-slate-500 dark:text-slate-400">
                                            {stats.inscritos} de {stats.total}
                                        </span>
                                    </div>
                                    {teamStats.leads > 0 && (
                                        <div className="flex items-center gap-1.5 mt-1.5 text-[10.5px] flex-wrap">
                                            <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full font-extrabold tabular-nums ${teamComparison.isAbove ? "bg-emerald-100 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300" : "bg-rose-100 dark:bg-rose-950/60 text-rose-700 dark:text-rose-300"}`}>
                                                {teamComparison.isAbove ? <ArrowUp className="h-2.5 w-2.5" /> : <ArrowDown className="h-2.5 w-2.5" />}
                                                {deltaRounded} pts
                                            </span>
                                            <span className="text-slate-500 dark:text-slate-400">
                                                vs equipo · <b className="text-slate-700 dark:text-slate-200 tabular-nums">{Math.round(teamComparison.teamConversion)}%</b>
                                            </span>
                                        </div>
                                    )}
                                    <div className="relative mt-2.5 h-1.5 w-full rounded-full bg-white/70 dark:bg-slate-900/60 overflow-hidden ring-1 ring-white/60 dark:ring-slate-800">
                                        <motion.div
                                            initial={{ width: 0 }}
                                            animate={{ width: `${Math.min(100, conversionRounded)}%` }}
                                            transition={{ duration: 1, ease: "easeOut" }}
                                            className="h-full rounded-full"
                                            style={{ backgroundColor: cap.accent }}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* KPIs 4 cards */}
                            <div className="grid grid-cols-4 gap-2">
                                {[
                                    { label: "Leads", value: stats.total, icon: Users, color: "#0ea5e9", bg: "bg-sky-50 dark:bg-sky-950/30", border: "border-sky-100 dark:border-sky-900/40", text: "text-sky-700 dark:text-sky-300" },
                                    { label: "Inscritos", value: stats.inscritos, icon: Trophy, color: "#10b981", bg: "bg-emerald-50 dark:bg-emerald-950/30", border: "border-emerald-100 dark:border-emerald-900/40", text: "text-emerald-700 dark:text-emerald-300" },
                                    { label: "Seguim.", value: stats.followUp, icon: Clock, color: "#f59e0b", bg: "bg-amber-50 dark:bg-amber-950/30", border: "border-amber-100 dark:border-amber-900/40", text: "text-amber-700 dark:text-amber-300" },
                                    { label: "Citas", value: stats.citas, icon: Calendar, color: "#8b5cf6", bg: "bg-violet-50 dark:bg-violet-950/30", border: "border-violet-100 dark:border-violet-900/40", text: "text-violet-700 dark:text-violet-300" },
                                ].map((m) => {
                                    const Icon = m.icon
                                    return (
                                        <div key={m.label} className={`rounded-xl border ${m.bg} ${m.border} px-2.5 py-2 hover:-translate-y-0.5 hover:shadow-sm transition-all`}>
                                            <div className="flex items-center gap-1.5 mb-1">
                                                <Icon className="h-3 w-3" style={{ color: m.color }} />
                                                <p className={`text-[9px] uppercase tracking-wider font-extrabold ${m.text} opacity-80`}>
                                                    {m.label}
                                                </p>
                                            </div>
                                            <p className={`text-xl font-extrabold tabular-nums ${m.text} leading-none`}>
                                                {m.value}
                                            </p>
                                        </div>
                                    )
                                })}
                            </div>

                            {/* Mini-stats: tasa de contacto + tasa de cita */}
                            {stats.total > 0 && (
                                <div className="grid grid-cols-2 gap-2">
                                    <div className="rounded-xl border border-slate-200/70 dark:border-slate-800 bg-white dark:bg-slate-900/60 p-2.5">
                                        <div className="flex items-center justify-between mb-1.5">
                                            <span className="text-[9.5px] uppercase tracking-wider font-bold text-slate-400 dark:text-slate-500">
                                                Tasa contacto
                                            </span>
                                            <span className="text-[11px] font-extrabold tabular-nums text-slate-700 dark:text-slate-200">
                                                {Math.round(stats.tasaContacto)}%
                                            </span>
                                        </div>
                                        <div className="h-1 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
                                            <motion.div initial={{ width: 0 }} animate={{ width: `${stats.tasaContacto}%` }} transition={{ duration: 0.7 }} className="h-full rounded-full bg-cyan-500" />
                                        </div>
                                    </div>
                                    <div className="rounded-xl border border-slate-200/70 dark:border-slate-800 bg-white dark:bg-slate-900/60 p-2.5">
                                        <div className="flex items-center justify-between mb-1.5">
                                            <span className="text-[9.5px] uppercase tracking-wider font-bold text-slate-400 dark:text-slate-500">
                                                Tasa cita
                                            </span>
                                            <span className="text-[11px] font-extrabold tabular-nums text-slate-700 dark:text-slate-200">
                                                {Math.round(stats.tasaCitas)}%
                                            </span>
                                        </div>
                                        <div className="h-1 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
                                            <motion.div initial={{ width: 0 }} animate={{ width: `${stats.tasaCitas}%` }} transition={{ duration: 0.7 }} className="h-full rounded-full bg-violet-500" />
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Pipeline */}
                            {stats.total > 0 && (
                                <div className="rounded-xl border border-slate-200/70 dark:border-slate-800 bg-white dark:bg-slate-900/60 p-3.5">
                                    <div className="flex items-center justify-between mb-3">
                                        <div className="flex items-center gap-1.5">
                                            <Target className="h-3 w-3 text-slate-400" />
                                            <h4 className="text-[10px] uppercase tracking-[0.18em] font-extrabold text-slate-500 dark:text-slate-400">
                                                Pipeline
                                            </h4>
                                        </div>
                                        <span className="text-[9.5px] text-slate-400 tabular-nums">
                                            {stats.total} prospectos
                                        </span>
                                    </div>
                                    <div className="space-y-2">
                                        {funnel.map((f) => {
                                            const Icon = f.icon
                                            return (
                                                <div key={f.label} className="flex items-center gap-2">
                                                    <div className="flex h-6 w-6 items-center justify-center rounded-md flex-shrink-0" style={{ backgroundColor: `${f.color}15`, color: f.color }}>
                                                        <Icon className="h-3 w-3" />
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center justify-between mb-0.5">
                                                            <span className="text-[11px] font-bold text-slate-700 dark:text-slate-200">
                                                                {f.label}
                                                            </span>
                                                            <span className="text-[11.5px] font-extrabold tabular-nums" style={{ color: f.color }}>
                                                                {f.value}
                                                            </span>
                                                        </div>
                                                        <div className="h-1 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
                                                            <motion.div initial={{ width: 0 }} animate={{ width: `${f.pct}%` }} transition={{ duration: 0.8 }} className="h-full rounded-full" style={{ backgroundColor: f.color }} />
                                                        </div>
                                                    </div>
                                                </div>
                                            )
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* COLUMNA DERECHA (5): Insights + Logros + Lista */}
                        <div className="lg:col-span-5 space-y-3">

                            {/* INSIGHTS — lo más útil para supervisor */}
                            {insights.length > 0 && (
                                <div className="rounded-xl border border-slate-200/70 dark:border-slate-800 bg-white dark:bg-slate-900/60 overflow-hidden">
                                    <div className="px-3.5 py-2 border-b border-slate-100 dark:border-slate-800 bg-slate-50/70 dark:bg-slate-900/80 flex items-center gap-1.5">
                                        <AlertCircle className="h-3 w-3 text-slate-400" />
                                        <h4 className="text-[10px] uppercase tracking-[0.18em] font-extrabold text-slate-500 dark:text-slate-400">
                                            Insights
                                        </h4>
                                    </div>
                                    <div className="divide-y divide-slate-100 dark:divide-slate-800">
                                        {insights.map((i, idx) => {
                                            const Icon = i.icon
                                            const tone = {
                                                good: { color: "#10b981", bg: "bg-emerald-50/40 dark:bg-emerald-950/10", icon: "bg-emerald-100 dark:bg-emerald-950/40" },
                                                warn: { color: "#f59e0b", bg: "bg-amber-50/40 dark:bg-amber-950/10", icon: "bg-amber-100 dark:bg-amber-950/40" },
                                                bad: { color: "#ef4444", bg: "bg-rose-50/40 dark:bg-rose-950/10", icon: "bg-rose-100 dark:bg-rose-950/40" },
                                                info: { color: "#64748b", bg: "bg-slate-50/40 dark:bg-slate-900/40", icon: "bg-slate-100 dark:bg-slate-800" },
                                            }[i.tone] || {}
                                            return (
                                                <div key={idx} className={`px-3.5 py-2 flex items-center gap-2 ${tone.bg}`}>
                                                    <div className={`flex h-6 w-6 items-center justify-center rounded-md flex-shrink-0 ${tone.icon}`}>
                                                        <Icon className="h-3 w-3" style={{ color: tone.color }} />
                                                    </div>
                                                    <span className="text-[11px] font-semibold text-slate-700 dark:text-slate-200 leading-tight">
                                                        {i.label}
                                                    </span>
                                                </div>
                                            )
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* Logros */}
                            {achievements.length > 0 && (
                                <div className="rounded-xl border border-amber-200/60 dark:border-amber-900/40 bg-gradient-to-br from-amber-50/80 to-white dark:from-amber-950/20 dark:to-slate-900/60 p-3">
                                    <div className="flex items-center gap-1.5 mb-2">
                                        <Sparkles className="h-3 w-3 text-amber-600 dark:text-amber-400" />
                                        <h4 className="text-[10px] uppercase tracking-[0.18em] font-extrabold text-amber-700 dark:text-amber-400">
                                            Logros del mes
                                        </h4>
                                    </div>
                                    <div className="flex flex-wrap gap-1">
                                        {achievements.map((a) => {
                                            const Icon = a.icon
                                            return (
                                                <div key={a.label} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border bg-white dark:bg-slate-900" style={{ borderColor: `${a.color}40`, color: a.color }}>
                                                    <Icon className="h-2.5 w-2.5" />
                                                    {a.label}
                                                </div>
                                            )
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* Últimos prospectos */}
                            {recentLeads.length > 0 && (
                                <div className="rounded-xl border border-slate-200/70 dark:border-slate-800 bg-white dark:bg-slate-900/60 overflow-hidden">
                                    <div className="px-3.5 py-2 border-b border-slate-100 dark:border-slate-800 bg-slate-50/70 dark:bg-slate-900/80 flex items-center justify-between">
                                        <div className="flex items-center gap-1.5">
                                            <Users className="h-3 w-3 text-slate-400" />
                                            <h4 className="text-[10px] uppercase tracking-[0.18em] font-extrabold text-slate-500 dark:text-slate-400">
                                                Últimos prospectos
                                            </h4>
                                        </div>
                                        <span className="text-[9.5px] text-slate-400 tabular-nums">
                                            {recentLeads.length}
                                        </span>
                                    </div>
                                    <div className="divide-y divide-slate-100 dark:divide-slate-800">
                                        {recentLeads.map((l) => {
                                            const st = normalizeStatus(l.estado_lead)
                                            const stLabel = STATUS_LABELS[st] || st || "—"
                                            const isInscrito = COMPLETED_STATUSES.has(st)
                                            return (
                                                <div key={l.id} className="px-3 py-2 flex items-center gap-2 hover:bg-slate-50/70 dark:hover:bg-slate-800/40 transition-colors">
                                                    <div className="flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-700 text-slate-700 dark:text-slate-200 text-[10px] font-bold flex-shrink-0">
                                                        {(l.nombre_tutor || "?").charAt(0).toUpperCase()}
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-[11.5px] font-bold text-slate-900 dark:text-slate-100 truncate leading-tight">
                                                            {l.nombre_tutor || "Sin nombre"}
                                                        </p>
                                                        <div className="flex items-center gap-1 mt-0.5">
                                                            <span className={`inline-flex items-center px-1 py-px rounded text-[8.5px] font-bold ${isInscrito ? "bg-emerald-100 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300" : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300"}`}>
                                                                {stLabel}
                                                            </span>
                                                            {l.nivel_educativo && <span className="text-[9px] text-slate-400 truncate">{l.nivel_educativo}</span>}
                                                        </div>
                                                    </div>
                                                    <span className="text-[9.5px] text-slate-400 tabular-nums flex-shrink-0">
                                                        {l.created_at ? new Date(l.created_at).toLocaleDateString("es-MX", { day: "numeric", month: "short" }) : "—"}
                                                    </span>
                                                </div>
                                            )
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* ═══ FOOTER ═══ */}
                    <div className="flex-shrink-0 px-4 py-2.5 border-t border-slate-100 dark:border-slate-800/80 bg-slate-50/50 dark:bg-slate-900/50 flex items-center justify-between gap-2">
                        <span className="text-[10.5px] text-slate-500 dark:text-slate-400">
                            <b className="text-slate-800 dark:text-slate-200 tabular-nums">{advisorLeads.length}</b>{" "}
                            prospecto{advisorLeads.length !== 1 ? "s" : ""} en total
                        </span>
                        <button
                            type="button"
                            onClick={handleExport}
                            disabled={advisorLeads.length === 0}
                            className="inline-flex items-center gap-1.5 h-7 px-3 rounded-lg text-[11px] font-extrabold text-white shadow-sm disabled:opacity-40 disabled:cursor-not-allowed transition-all hover:shadow-md"
                            style={{ backgroundColor: cap.accent }}
                        >
                            <Download className="h-3 w-3" />
                            Exportar CSV
                        </button>
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>,
        document.body,
    )
}
